import { FileIdsValue, MatchingFormat, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import Language from '../../entity/Language';
import PageInfo from '../../entity/PageInfo';
import { LatestProjectFilterKey, ProjectFilterKey } from '../../entity/ProjectFilter';
import ProjectOrderTarget from '../../entity/ProjectOrderTarget';
import { valuesToJSON } from '../../graphql/dynamic_schema';
import { getProjectForBrief, ProjectForBrief } from '../../graphql/project';
import {
  DynamicSchemaDefinition,
  IssueCommentForProjectFragment,
  IssueForProjectFragment,
  Maybe,
  ProjectDetailsForEditFragment,
  ProjectDetailsForShowFragment,
  ProjectForAllOfOrganizationFragment,
  ProjectType,
} from './../../generated/graphql';

const issueCommentForProjectFragment = gql`
  fragment IssueCommentForProject on IssueComment {
    id
    body
    createdAt
    updatedAt
    user {
      ... on UserDirector {
        id
        name
        email
        profileImageUrl
      }
      ... on UserOptimizer {
        id
        name
        email
        profileImageUrl
      }
    }
  }
`;

// 制作物一覧内の読み取り専用の場合に使用
const issueForProjectFragment = gql`
  ${issueCommentForProjectFragment}
  fragment IssueForProject on Issue {
    id
    name
    comments {
      ...IssueCommentForProject
    }
  }
`;

export const creativeBriefDefinitionFragment = gql`
  fragment CreativeBriefDefinitionForProject on DynamicSchemaDefinition {
    id
    sections {
      id
      label
      description
      meta
      fields {
        ... on DynamicSchemaDefinitionSelectOne {
          key
          type
          label
          choices {
            key
            label
          }
          helperText
        }
        ... on DynamicSchemaDefinitionSelectMultiple {
          key
          type
          label
          choices {
            key
            label
          }
          helperText
          isRequiredToSelectAnything
        }
        ... on DynamicSchemaDefinitionToggle {
          key
          type
          label
          checkedControlLabel
          uncheckedControlLabel
        }
        ... on DynamicSchemaDefinitionSingleLineText {
          key
          type
          label
          placeholder
          helperText
          isRequired
        }
        ... on DynamicSchemaDefinitionMultiLineText {
          key
          type
          label
          placeholder
          helperText
          isRequired
        }
        ... on DynamicSchemaDefinitionPositiveInteger {
          key
          type
          label
          helperText
          isRequired
        }
        ... on DynamicSchemaDefinitionFiles {
          key
          type
          label
          helperText
        }
        ... on DynamicSchemaDefinitionFixed {
          key
          type
          label
          value
        }
        ... on DynamicSchemaDefinitionFileIds {
          key
          type
          label
        }
      }
    }
  }
`;

export const assetCategoryDefinitionFragment = gql`
  fragment AssetCategoryDefinitionForProject on DynamicSchemaDefinition {
    id
    sections {
      id
      label
      description
      meta
      fields {
        ... on DynamicSchemaDefinitionSelectOne {
          key
          type
          label
          choices {
            key
            label
          }
          helperText
        }
        ... on DynamicSchemaDefinitionSingleLineText {
          key
          type
          label
          placeholder
          helperText
          isRequired
        }
        ... on DynamicSchemaDefinitionMultiLineText {
          key
          type
          label
          placeholder
          helperText
          isRequired
        }
        ... on DynamicSchemaDefinitionFileIds {
          key
          type
          label
        }
      }
    }
  }
`;

const creativeFormatForShowPageFragment = gql`
  fragment CreativeFormatForShow on CreativeFormat {
    id
    network {
      id
      key
      name
    }
    placement {
      id
      key
      name
      basicMerchandises {
        isResizable
        creativeType {
          id
          key
          name
        }
      }
    }
    creativeType {
      requiredPoints {
        key
        point
      }
      id
      key
      name
    }
    selectedPricingMerchandiseOptions {
      id
      name
      helpUrlForDirector
    }
    attribute {
      aspectRatio {
        supportingAspectRatioId
        aspectRatio {
          width
          height
        }
        size {
          width
          height
        }
      }
      playbackTime {
        supportingPlaybackTimeId
        seconds
      }
    }
    resizeBaseId
  }
`;

const creationForShowPageFragment = gql`
  fragment CreationForShow on Creation {
    id
    latestDeliverableUrl
    latestDeliverableSubmittedAt
    modificationRequestExpired
    assignmentId
    assignee {
      id
      name
      profileImageUrl
    }
    deliverables {
      id
      creative {
        id
        file {
          id
          originalFileName
          url
          mimeType
          size
        }
      }
      attachedThumbnail {
        file {
          url
        }
      }
      uploadedAdPlatformKeys
      intent
      state
      createdAt
      stateUpdatedAt
    }
    modificationRequests {
      id
      bucketId
      state
      targetDeliverableId
      feeType
      createdAt
      submittedAt
      approvedAt
      deliveryDue
      feedback {
        id
        body
        createdAt
      }
      sender {
        id
        name
        profileImageUrl
      }
      items {
        id
        type
        typeName
        assetIds
        body
        metadata
        sender {
          id
          name
          profileImageUrl
        }
        issue {
          id
          name
          resolved
          comments {
            id
            body
            assetIds
            createdAt
            user {
              ... on UserDirector {
                id
                name
                email
                profileImageUrl
              }
              ... on UserOptimizer {
                id
                name
                email
                profileImageUrl
              }
            }
          }
        }
      }
      deliverables {
        id
        createdAt
        creative {
          id
          file {
            id
            originalFileName
            url
            mimeType
            size
          }
        }
        attachedThumbnail {
          id
          file {
            id
            originalFileName
            url
            mimeType
            size
          }
        }
        intent
      }
    }
    creativeFormat {
      id
      network {
        id
        key
        name
      }
      placement {
        id
        key
        name
      }
      creativeType {
        id
        key
        name
      }
      attribute {
        aspectRatio {
          supportingAspectRatioId
          aspectRatio {
            width
            height
          }
          size {
            width
            height
          }
        }
        playbackTime {
          supportingPlaybackTimeId
          seconds
        }
      }
      resizeBaseId
    }
    state
    proposed
    createdAt
    stateUpdatedAt
    submittedAt
    finalized
  }
`;

const creationSetForShowPageFragment = gql`
  ${creativeFormatForShowPageFragment}
  ${creationForShowPageFragment}
  fragment CreationSetForShow on CreationSet {
    id
    offerId
    creativeFormats {
      ...CreativeFormatForShow
    }
    creations {
      ...CreationForShow
    }
    amount
    additionalAmount
    deliveryDueDate
    deliveryDueDays
    expressDueDays
    finalizedAt
  }
`;

// 指示書編集時に使用
const projectDetailsWithoutPricingDataFragment = gql`
  ${creativeBriefDefinitionFragment}
  ${assetCategoryDefinitionFragment}
  ${creationSetForShowPageFragment}
  ${issueForProjectFragment}
  fragment ProjectDetailsForEdit on Project {
    id
    versionId
    matchingFormat
    organization {
      id
      name
      pointBalance
      features {
        canOpenOfferWhenDisagreed
      }
      businessRegion {
        key
        idBusinessRegion
        timeZone
      }
    }
    originId
    parentId
    title
    state
    finalized
    deliveryDueDate
    createdBy {
      id
      name
      profileImageUrl
    }
    designInspirationCase {
      id
      name
      url
      thumbnailUrl
    }
    projectType {
      id
      published
      version
      title
      description
      thumbnailImageUrl
      sampleVideoUrl
      expectedDeliveryPeriod
      outputSpecification
      regulation
      creativeBriefDefinition {
        ...CreativeBriefDefinitionForProject
      }
      assetCategoryDefinition {
        ...AssetCategoryDefinitionForProject
      }
      supportingCreativeFormatNetworks {
        id
        key
        name
        placements {
          id
          key
          name
          basicMerchandises {
            isResizable
            creativeType {
              id
              key
              name
            }
          }
          creativeTypes {
            id
            key
            name
            attribute {
              aspectRatio {
                id
                availables {
                  aspectRatio {
                    width
                    height
                  }
                  presetSize {
                    width
                    height
                  }
                }
              }
              playbackTime {
                id
                availables {
                  seconds
                }
              }
            }
          }
        }
      }
    }
    creativeBriefDefinition {
      ...CreativeBriefDefinitionForProject
    }
    creativeBriefValues
    bucketId
    assetCategoryDefinition {
      ...AssetCategoryDefinitionForProject
    }
    assetCategoryValues
    creationSet {
      ...CreationSetForShow
    }
    performanceReports {
      id
      adAccount {
        id
        network
        accountId
        name
      }
      lastUpdatedAt
      campaigns {
        id
        name
      }
      periodPatterns
      metrics
      comparisonTargets
      dimensions
    }
    createdAt
    submittedAt
    adVideos {
      adVideoId
      analyzingState
    }
    issue {
      ...IssueForProject
    }
  }
`;

// 制作物一覧内の読み取り専用の場合に使用
const projectDetailsForShowPageFragment = gql`
  ${creativeBriefDefinitionFragment}
  ${assetCategoryDefinitionFragment}
  ${creationSetForShowPageFragment}
  ${issueForProjectFragment}
  fragment ProjectDetailsForShow on Project {
    id
    hasChanges
    versionId
    matchingFormat
    organization {
      id
      name
      pointBalance
      features {
        canOpenOfferWhenDisagreed
      }
      businessRegion {
        key
        idBusinessRegion
        timeZone
      }
    }
    originId
    parentId
    title
    state
    finalized
    deliveryDueDate
    createdBy {
      id
      name
      profileImageUrl
    }
    designInspirationCase {
      id
      name
      url
      thumbnailUrl
    }
    projectType {
      id
      published
      version
      title
      description
      thumbnailImageUrl
      sampleVideoUrl
      expectedDeliveryPeriod
      outputSpecification
      regulation
    }
    creativeBriefDefinition {
      ...CreativeBriefDefinitionForProject
    }
    creativeBriefValues
    bucketId
    assetCategoryDefinition {
      ...AssetCategoryDefinitionForProject
    }
    assetCategoryValues
    creationSet {
      ...CreationSetForShow
    }
    performanceReports {
      id
      adAccount {
        id
        network
        accountId
        name
      }
      lastUpdatedAt
      campaigns {
        id
        name
      }
      periodPatterns
      metrics
      comparisonTargets
      dimensions
    }
    createdAt
    submittedAt
    adVideos {
      adVideoId
      analyzingState
    }
    issue {
      ...IssueForProject
    }
  }
`;

const projectForAllOfOrganizationFragment = gql`
  fragment ProjectForAllOfOrganization on Project {
    id
    title
    state
    finalized
    deliveryDueDate
    projectType {
      id
      title
    }
    createdBy {
      id
      name
      profileImageUrl
    }
    matchingFormat
    createdAt
    updatedAt
    submittedAt
    progressSummary {
      orderCount
      approvedCount
      modificationRequestCount
      modificationRequestFeedbackCount
    }
  }
`;

class ProjectWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public getByIdForEdit = async (
    id: string,
    language: Language,
  ): Promise<ProjectForBrief & ProjectDetailsForEditFragment> => {
    const result = await this.graphqlClient.query(
      gql`
        ${projectDetailsWithoutPricingDataFragment}
        query getByIdForEdit($id: ID!, $language: ID) {
          project(id: $id, language: $language) {
            ...ProjectDetailsForEdit
          }
        }
      `,
      {
        id,
        language,
      },
    );
    if (result.project === null) {
      throw new ProjectNotFound({
        id,
      });
    }
    const project = result.project as ProjectDetailsForEditFragment;
    return getProjectForBrief(project);
  };

  public getByIdForShowPage = async (
    id: string,
    language: Language,
  ): Promise<ProjectForBrief & ProjectDetailsForShowFragment> => {
    const result = await this.graphqlClient.query(
      gql`
        ${projectDetailsForShowPageFragment}
        query getProjectByIdForShowPage($id: ID!, $language: ID) {
          project(id: $id, language: $language) {
            ...ProjectDetailsForShow
          }
        }
      `,
      {
        id,
        language,
      },
    );
    if (result.project === null) {
      throw new ProjectNotFound({
        id,
      });
    }

    const project = result.project as ProjectDetailsForShowFragment;
    return getProjectForBrief(project);
  };

  public getAllOfOrganization = async (
    organizationId: string,
    projectFilterKey: ProjectFilterKey | LatestProjectFilterKey,
    projectOrderTarget: ProjectOrderTarget,
    cursor?: string,
    perPage?: number,
  ): Promise<{ projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo }> => {
    const defaultPerPage = 30;
    const filterKey = projectFilterKey === 'notDraft' ? 'in_progress' : projectFilterKey;

    const result = await this.graphqlClient.query(
      gql`
        ${projectForAllOfOrganizationFragment}
        query getProjectsByOrganization(
          $organizationId: ID!
          $order: ProjectOrder
          $filter: ProjectFilter
          $after: String
          $first: Int
        ) {
          projectsByOrganization(
            organizationId: $organizationId
            order: $order
            filter: $filter
            after: $after
            first: $first
          ) {
            nodes {
              ...ProjectForAllOfOrganization
            }
            pageInfo {
              endCursor
              hasNextPage
              hasPreviousPage
              startCursor
            }
          }
        }
      `,
      {
        organizationId,
        order: {
          target: projectOrderTarget,
          direction: 'desc',
        },
        filter: {
          filterKey,
        },
        after: cursor,
        first: perPage || defaultPerPage,
      },
    );

    if (!Array.isArray(result.projectsByOrganization.nodes)) {
      throw new Error('message.fetch_creations_has_failed');
    }

    return {
      projects: result.projectsByOrganization.nodes as Array<ProjectForAllOfOrganizationFragment>,
      pageInfo: result.projectsByOrganization.pageInfo as PageInfo,
    };
  };

  public createAsDraft = async ({
    title,
    matchingFormat,
    projectType,
    creativeBriefValue,
    assetCategoryValue,
    organizationId,
    parentProjectId,
    designInspirationCaseId,
  }: {
    title: string;
    matchingFormat: MatchingFormat;
    projectType: Pick<ProjectType, 'id'> & {
      creativeBriefDefinition: Pick<DynamicSchemaDefinition, 'id'>;
      assetCategoryDefinition?: Maybe<Pick<DynamicSchemaDefinition, 'id'>>;
    };
    creativeBriefValue: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
    assetCategoryValue: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
    organizationId: string;
    parentProjectId?: string;
    designInspirationCaseId?: string;
  }): Promise<string> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation createProjectAsDraft2(
          $title: String!
          $matchingFormat: MatchingFormat!
          $organizationId: ID!
          $projectTypeId: ID!
          $creationSet: CreationSetInput
          $creativeBriefDefinitionId: ID!
          $creativeBriefValues: DynamicSchemaValuesInput
          $assetCategoryDefinitionId: ID!
          $assetCategoryValues: DynamicSchemaValuesInput
          $parentProjectId: ID
          $designInspirationCaseId: ID
        ) {
          createProjectAsDraft(
            title: $title
            matchingFormat: $matchingFormat
            organization: $organizationId
            projectType: $projectTypeId
            creationSet: $creationSet
            creativeBriefDefinition: $creativeBriefDefinitionId
            creativeBriefValues: $creativeBriefValues
            assetCategoryDefinition: $assetCategoryDefinitionId
            assetCategoryValues: $assetCategoryValues
            parent: $parentProjectId
            designInspirationCaseId: $designInspirationCaseId
          ) {
            id
          }
        }
      `,
      {
        title,
        matchingFormat,
        projectTypeId: projectType.id,
        creationSet: null,
        creativeBriefDefinitionId: projectType.creativeBriefDefinition.id,
        creativeBriefValues: {
          fields: valuesToJSON(creativeBriefValue),
        },
        assetCategoryDefinitionId:
          projectType.assetCategoryDefinition != null ? projectType.assetCategoryDefinition.id : null,
        assetCategoryValues: {
          fields: valuesToJSON(assetCategoryValue),
        },
        organizationId,
        parentProjectId,
        designInspirationCaseId,
      },
    );

    return result.createProjectAsDraft.id;
  };

  public deleteDraftProject = async (project: { id: string }) => {
    await this.graphqlClient.query(
      gql`
        mutation deleteDraftProject($id: ID!) {
          deleteProject(id: $id) {
            success
          }
        }
      `,
      { id: project.id },
    );
  };

  public createProjectIssue = async (
    projectId: string,
    name: string,
    body: string,
  ): Promise<IssueForProjectFragment> => {
    const result = await this.graphqlClient.query(
      gql`
        ${issueForProjectFragment}
        mutation createProjectIssue($projectId: ID!, $name: String!, $body: String!) {
          createProjectIssue(projectId: $projectId, name: $name, body: $body) {
            ...IssueForProject
          }
        }
      `,
      { projectId, name, body },
    );

    return result.createProjectIssue as IssueForProjectFragment;
  };

  public createProjectIssueComment = async (issueId: string, body: string): Promise<IssueCommentForProjectFragment> => {
    // eslint-disable-next-line no-useless-catch
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${issueCommentForProjectFragment}
          mutation createProjectIssueComment($issueId: ID!, $body: String!) {
            createProjectIssueComment(issueId: $issueId, body: $body) {
              ...IssueCommentForProject
            }
          }
        `,
        { issueId, body },
      );

      return result.createProjectIssueComment as IssueCommentForProjectFragment;
    } catch (err) {
      throw err;
    }
  };

  public updateProjectIssueComment = async (
    commentId: string,
    body: string,
  ): Promise<IssueCommentForProjectFragment> => {
    // eslint-disable-next-line no-useless-catch
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${issueCommentForProjectFragment}
          mutation updateProjectIssueComment($commentId: ID!, $body: String!) {
            updateProjectIssueComment(commentId: $commentId, body: $body) {
              ...IssueCommentForProject
            }
          }
        `,
        { commentId, body },
      );

      return result.updateProjectIssueComment as IssueCommentForProjectFragment;
    } catch (err) {
      throw err;
    }
  };

  public deleteProjectIssueComment = async (commentId: string): Promise<IssueForProjectFragment> => {
    // eslint-disable-next-line no-useless-catch
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${issueForProjectFragment}
          mutation deleteProjectIssueComment($commentId: ID!) {
            deleteProjectIssueComment(commentId: $commentId) {
              ...IssueForProject
            }
          }
        `,
        { commentId },
      );

      return result.deleteProjectIssueComment as IssueForProjectFragment;
    } catch (err) {
      throw err;
    }
  };
}

export class ProjectNotFound extends Error {
  public readonly name = 'ProjectNotFound';
  public readonly message: string;
  public readonly id: string;

  constructor({ id }: { id: string }) {
    super();

    this.id = id;
    this.message = `The project (id = ${id}) is not found.`;
  }
}

export default ProjectWebApi;
